// Unpublished Work © 2024-2025 Deere & Company.

import {JDF_APP_NAMES} from './jdf-parent-app';
import {useSelector, shallowEqual} from 'react-redux';
import {useMemo} from 'react';
import {JDF_COUNTRIES_LIST} from './jdf-country-list';
import {customerTypes} from '../../state/enum/customer-type';

export const isJdfFlow = (jdfFlow, parentAppName, country) => jdfFlow && JDF_APP_NAMES.includes(parentAppName) && JDF_COUNTRIES_LIST.includes(country);

export const useIsJdfFlow = () => {
    const {
        jdfFlow, parentAppName
    } = useSelector((state) => state.configuration, shallowEqual);

    const country = useSelector((state) => state.user?.country);

    return useMemo(() => isJdfFlow(jdfFlow, parentAppName, country), [jdfFlow, parentAppName, country]);
};

const checkJDFAddresses = ({communication}) => {
    const {
        mailingAddress,
        physicalAddress
    } = communication;

    return Boolean(physicalAddress && mailingAddress);
};

const checkJDFPhoneNumber = ({communication}) => {
    const {
        homePhone,
        workPhone,
        mobilePhone
    } = communication;

    return Boolean(homePhone || workPhone || mobilePhone);
};

const checkJDFCustomerRequireFields = (countryCode) => {
    const usFields = {
        [customerTypes.INDIVIDUAL]: (jdfCustomer) => [
            jdfCustomer.customerName.firstName,
            jdfCustomer.customerName.lastName,
            jdfCustomer.birthDate,
            jdfCustomer.legalNameVerInd || jdfCustomer.customerName.middleName,
            jdfCustomer.governmentTaxIds?.[0]?.taxId
        ].every((condition) => condition),
        [customerTypes.BUSINESS]: (jdfCustomer) => [
            jdfCustomer.customerName.businessName1,
            jdfCustomer.customerName.businessType,
            jdfCustomer.governmentTaxIds?.[0]?.taxId,
            jdfCustomer?.filingDetails?.filingStateCode
        ].every((condition) => condition),
        [customerTypes.BUSINESS_CONTACT]: (jdfCustomer) => [
            jdfCustomer.customerName.businessName1,
            jdfCustomer.customerName.businessType,
            jdfCustomer.governmentTaxIds?.[0]?.taxId,
            jdfCustomer?.filingDetails?.filingStateCode
        ].every((condition) => condition)
    };

    const caFields = {
        [customerTypes.INDIVIDUAL]: (jdfCustomer) => [
            jdfCustomer.customerName.firstName,
            jdfCustomer.customerName.lastName,
            jdfCustomer.birthDate,
            jdfCustomer.legalNameVerInd || jdfCustomer.customerName.middleName
        ].every((condition) => condition),
        [customerTypes.BUSINESS]: (jdfCustomer) => [
            jdfCustomer.customerName.businessName1,
            jdfCustomer.customerName.businessType,
            jdfCustomer?.filingDetails?.filingStateCode
        ].every((condition) => condition),
        [customerTypes.BUSINESS_CONTACT]: (jdfCustomer) => [
            jdfCustomer.customerName.businessName1,
            jdfCustomer.customerName.businessType,
            jdfCustomer?.filingDetails?.filingStateCode
        ].every((condition) => condition)
    };

    return countryCode === 'US' ? usFields : caFields;
};

export const isJDFCustomerRequiredFieldsPresent = (jdfCustomer, countryCode) => [
    checkJDFAddresses(jdfCustomer),
    checkJDFPhoneNumber(jdfCustomer),
    checkJDFCustomerRequireFields(countryCode)[jdfCustomer.customerType](jdfCustomer),
    jdfCustomer?.communication?.email?.emailAddress
].every((condition) => condition);
